import { useState } from 'react';
import { CopywriterResult } from '../types';

const WEBHOOK_URL = 'https://n8n-n8n-start.qlgk5q.easypanel.host/webhook/77a5b860-30c1-4946-bc2e-bcc33fc4b5b2/chat';

export const useCopywriterAgent = () => {
  const [niche, setNiche] = useState('');
  const [descInput, setDescInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<CopywriterResult | null>(null);

  const parseJsonFromString = (str: string): any => {
    try {
      // Remove qualquer texto antes do primeiro '{'
      const startIdx = str.indexOf('{');
      // Remove qualquer texto depois do último '}'
      const endIdx = str.lastIndexOf('}') + 1;
      
      if (startIdx === -1 || endIdx === 0) {
        // Tenta buscar por padrões alternativos
        const arrayStartIdx = str.indexOf('[');
        const arrayEndIdx = str.lastIndexOf(']') + 1;
        
        if (arrayStartIdx !== -1 && arrayEndIdx > arrayStartIdx) {
          const arrayStr = str.slice(arrayStartIdx, arrayEndIdx);
          return JSON.parse(arrayStr);
        }
        
        throw new Error('JSON inválido na resposta');
      }

      const jsonStr = str.slice(startIdx, endIdx);
      const parsed = JSON.parse(jsonStr);
      return parsed;
    } catch (e) {
      console.error('Erro ao parsear JSON:', e);
      throw new Error('Falha ao extrair JSON da resposta');
    }
  };

  // Função recursiva para tentar localizar um objeto que contenha as chaves esperadas
  const deepExtractResult = (input: any): any | null => {
    console.log('deepExtractResult chamada com:', typeof input, input);
    
    if (!input) return null;

    // Se for string, tentar extrair JSON e analisar novamente
    if (typeof input === 'string') {
      try {
        const parsed = parseJsonFromString(input);
        console.log('String parseada para JSON:', parsed);
        return deepExtractResult(parsed);
      } catch (error) {
        console.log('Erro ao parsear string como JSON:', error);
        return null;
      }
    }

    // Se for objeto, verificar se possui pelo menos algumas chaves necessárias
    if (typeof input === 'object') {
      const expectedKeys = ['title', 'features', 'description', 'benefits', 'triggers'];
      const alternativeKeys = ['name', 'produto', 'caracteristicas', 'descricao', 'desc', 'beneficios', 'gatilhos'];
      const allKeys = [...expectedKeys, ...alternativeKeys];
      
      const foundKeys = allKeys.filter(k => Object.prototype.hasOwnProperty.call(input, k));
      
      console.log('Chaves encontradas no objeto:', foundKeys);
      
      // Se tem pelo menos 1 chave esperada, considerar válido (mais tolerante)
      if (foundKeys.length >= 1) {
        console.log('Objeto válido encontrado:', input);
        return input;
      }

      // Se não, iterar pelos valores
      for (const [key, value] of Object.entries(input)) {
        console.log(`Verificando propriedade: ${key}`, value);
        const found = deepExtractResult(value);
        if (found) return found;
      }
    }

    // Se for array, iterar pelos itens
    if (Array.isArray(input)) {
      for (const item of input) {
        const found = deepExtractResult(item);
        if (found) return found;
      }
    }

    return null;
  };

  const normalizeResponse = (data: any): CopywriterResult => {
    console.log('Normalizando resposta da API');
    
    // Tenta extração profunda sempre
    let content = deepExtractResult(data) || data;

    // Se ainda não for objeto completo, aplicar lógica de fallback existente
    if (typeof content !== 'object' || !content) {
      if (data.response) {
        content = typeof data.response === 'string' ? parseJsonFromString(data.response) : data.response;
      } else if (data.content) {
        content = typeof data.content === 'string' ? parseJsonFromString(data.content) : data.content;
      } else if (data.message) {
        content = typeof data.message === 'string' ? parseJsonFromString(data.message) : data.message;
      } else if (typeof data === 'string') {
        content = parseJsonFromString(data);
      }
    }

    // Normaliza e valida cada campo com suporte a campos alternativos
    const normalized: CopywriterResult = {
      title: String(content?.title || content?.name || content?.produto || '').trim(),
      features: Array.isArray(content?.features) 
        ? content.features.map((f: any) => String(f).trim()).filter(Boolean)
        : Array.isArray(content?.caracteristicas)
        ? content.caracteristicas.map((f: any) => String(f).trim()).filter(Boolean)
        : [],
      description: String(content?.description || content?.descricao || content?.desc || '').trim(),
      benefits: Array.isArray(content?.benefits)
        ? content.benefits.map((b: any) => String(b).trim()).filter(Boolean)
        : Array.isArray(content?.beneficios)
        ? content.beneficios.map((b: any) => String(b).trim()).filter(Boolean)
        : [],
      triggers: Array.isArray(content?.triggers)
        ? content.triggers.map((t: any) => String(t).trim()).filter(Boolean)
        : Array.isArray(content?.gatilhos)
        ? content.gatilhos.map((t: any) => String(t).trim()).filter(Boolean)
        : []
    };

    // Validação mais flexível - se pelo menos um campo estiver preenchido, mostra o resultado
    const hasAnyContent = normalized.title || 
                         normalized.features.length > 0 || 
                         normalized.description || 
                         normalized.benefits.length > 0 || 
                         normalized.triggers.length > 0;

    if (!hasAnyContent) {
      console.error('Dados normalizados sem conteúdo');
      throw new Error('Resposta da API não contém dados válidos');
    }

    // Sanitiza título removendo caracteres especiais
    if (normalized.title) {
      normalized.title = normalized.title
        .replace(/[^\w\sÀ-ÿ-]/g, '') // remove especiais, mantém hífen
        .replace(/\s+/g, ' ')          // colapsa espaços
        .trim();
    }

    return normalized;
  };

  const generateContent = async () => {
    if (!niche.trim() && !descInput.trim()) {
      setError('Preencha título ou descrição para gerar conteúdo');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Monta contexto dinâmico
      const details: string[] = [];
      if (niche.trim()) details.push(`Produto: "${niche}"`);
      if (descInput.trim()) details.push(`Descrição: "${descInput}"`);

      const prompt = `Como copywriter profissional, crie conteúdo persuasivo para:
${details.join('\n')}

Retorne APENAS um JSON válido com esta estrutura exata:
{
  "title": "título persuasivo do produto",
  "features": ["característica 1", "característica 2", "característica 3"],
  "description": "descrição persuasiva completa",
  "benefits": ["benefício 1", "benefício 2", "benefício 3"],
  "triggers": ["gatilho mental 1", "gatilho mental 2", "gatilho mental 3"]
}`;

      console.log('Enviando requisição para o webhook');

      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ 
          message: prompt
        }),
      });

      // Tratamento específico para erro 500
      if (response.status === 500) {
        throw new Error('Servidor temporariamente indisponível');
      }

      if (!response.ok) {
        throw new Error(`Erro HTTP ${response.status}: ${response.statusText}`);
      }

      let responseData;
      const contentType = response.headers.get('content-type') || '';
      
      if (contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        const textData = await response.text();
        responseData = parseJsonFromString(textData);
      }

      const normalizedResult = normalizeResponse(responseData);
      console.log('Resultado normalizado:', normalizedResult);
      setResult(normalizedResult);
      // Limpa campos após sucesso
      setNiche('');
      setDescInput('');

    } catch (err) {
      console.error('Erro detalhado:', err);
      
      // Determina se deve usar fallback ou mostrar erro
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      
      if (errorMessage.includes('500') || errorMessage.includes('indisponível')) {
        // Para erro 500, usar conteúdo inteligente baseado nos inputs
        const intelligentFallback: CopywriterResult = {
          title: niche.trim() ? `${niche.trim()} - Solução Profissional` : 'Produto Inovador Premium',
          features: descInput.trim() ? 
            descInput.split(/[.,;]/).map(f => f.trim()).filter(Boolean).slice(0, 3) : 
            ['Tecnologia de ponta', 'Design premium', 'Resultados garantidos'],
          description: `${niche.trim() || 'Este produto revolucionário'} ${descInput.trim() ? 'com ' + descInput.trim() + ' ' : ''}oferece uma experiência única no mercado. Desenvolvido com as mais avançadas tecnologias, garante resultados superiores e satisfação total dos usuários.`,
          benefits: [
            'Aumenta significativamente a produtividade',
            'Reduz custos operacionais em até 40%',
            'Entrega resultados profissionais imediatos'
          ],
          triggers: [
            'Promoção especial válida apenas hoje',
            'Estoque limitado - últimas unidades',
            'Garantia estendida de 24 meses inclusa'
          ]
        };
        
        setResult(intelligentFallback);
        setError('Servidor temporariamente ocupado. Conteúdo gerado localmente.');
      } else {
        // Para outros erros, usar fallback básico
        const basicFallback: CopywriterResult = {
          title: niche.trim() || 'Produto Exclusivo',
          features: ['Qualidade superior', 'Design moderno', 'Fácil utilização'],
          description: `${niche.trim() || 'Este produto'} representa inovação e qualidade. ${descInput.trim() || 'Com características únicas no mercado.'} Ideal para quem busca excelência e resultados.`,
          benefits: [
            'Economia de tempo',
            'Resultados comprovados',
            'Suporte especializado'
          ],
          triggers: [
            'Oferta por tempo limitado',
            'Apenas poucas unidades',
            'Bônus exclusivo incluso'
          ]
        };
        
        setResult(basicFallback);
        setError(`Problema na conexão: ${errorMessage}. Conteúdo alternativo gerado.`);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    niche,
    setNiche,
    descInput,
    setDescInput,
    loading,
    error,
    result,
    generateContent
  };
}; 